import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { createCreativeWorkspace, patchCreativeWorkspace, type CreativeWorkspace } from '@/api/studio-creative'
import { STUDIO_BASE } from '@/features/studio/lib/studio-paths'
import { AuthSessionExpiredError } from '@/api/authSession'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { toast } from '@/shared/lib/notify'

export function CreateCreativeWorkspaceModal({
  open,
  onClose,
  onCreated,
  editing,
}: {
  open: boolean
  onClose: () => void
  onCreated?: () => void
  editing?: CreativeWorkspace | null
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isEdit = editing != null
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    if (isEdit) {
      setTitle(editing.title ?? '')
      setSummary(editing.summary ?? '')
      return
    }
    setTitle('')
    setSummary('')
  }, [open, editing, isEdit])

  async function handleSubmit() {
    const trimmed = title.trim()
    if (!trimmed) {
      setError(t('creative.create.titleRequired'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      if (isEdit) {
        await patchCreativeWorkspace(editing.public_id, {
          title: trimmed,
          summary: summary.trim(),
        })
        onClose()
        onCreated?.()
        toast.success(t('creative.create.saved'))
        return
      }
      const row = await createCreativeWorkspace({ title: trimmed, summary: summary.trim() || undefined })
      onClose()
      onCreated?.()
      navigate(`${STUDIO_BASE}/creative/${row.public_id}/canvas`)
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      setError(resolveApiErrorMessage(e, isEdit ? 'creative.create.saveFailed' : 'creative.create.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={() => {
        if (submitting) return
        onClose()
      }}
      title={isEdit ? t('creative.create.editTitle') : t('creative.create.title')}
      footer={
        <>
          <ProtoBtn variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting
              ? t('common.processing')
              : isEdit
                ? t('common.save')
                : t('creative.create.submit')}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-rose-300">{error}</p> : null}
      <div className="space-y-4">
        <div>
          <ProtoLabel required>{t('creative.create.fieldTitle')}</ProtoLabel>
          <ProtoInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <ProtoLabel>{t('creative.create.fieldSummary')}</ProtoLabel>
          <ProtoTextarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} />
        </div>
      </div>
    </ProtoModal>
  )
}
